import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDidUpdate, useLoader } from '@equifinance/shared';
import useSearchApi from '../../api/useSearchApi';
import useChecklistApi from '../../api/useChecklistApi';
import useProductsApi, { HistoricData } from '../../api/useProductsApi';
import {
  Checklist,
  ChecklistNotePayload,
  defaultChecklist,
  useChecklistContext,
  ChecklistRequest,
  defaultSelectedProduct,
  ChecklistOverrideProductPayload,
  UploadChecklistDocumentPayload,
  SendOfferPayload,
  AvmSearchTypes,
} from '.';
import { useAccountContext } from '../account';

export function useChecklist(id: string) {
  const [checklist, setChecklist] = useState<Checklist>(defaultChecklist);
  const [historicData, setHistoricData] = useState<HistoricData>({
    productReferences: [],
  });
  const { fetchTransitionStatus } = useAccountContext();

  const incrementedUpdateCount = (prevUpdateCount: number) => (prevUpdateCount ? prevUpdateCount + 1 : 1);

  const updateChecklist = async (field: string, value: any, disableIncrementUpdateCount: boolean = false) => {
    setChecklist((prev) => {
      const clone = _.cloneDeep(prev);
      const newChecklist = _.set(clone, field, value);
      return {
        ...newChecklist,
        updateCount: (disableIncrementUpdateCount ? prev?.updateCount : incrementedUpdateCount(prev?.updateCount)),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
      };
    });
  };

  const {
    getChecklist, postNote, postOverrideProduct, uploadDocuments, postSendOffer, postCancelOffer, postResendOffer,
  } = useChecklistApi();
  const { getProductHistoric } = useProductsApi();

  const { postAVMSearch } = useSearchApi();

  const fetchChecklist = async () => {
    const [checklistResponse, historicDataResponse] = await Promise.allSettled([
      getChecklist(id),
      getProductHistoric(),
    ]);
    if (checklistResponse.status === 'fulfilled') {
      const checklistResult = checklistResponse.value;
      setChecklist((prev) => ({
        ...prev,
        ...checklistResult,
      }));
    }
    if (historicDataResponse.status === 'fulfilled') {
      const historicDataResult = historicDataResponse.value;
      setHistoricData(historicDataResult);
    }
  };

  const postNewNote = async ({ checklistSectionId, note }: ChecklistNotePayload) => {
    const newChecklist = await postNote({
      checklistSectionId,
      note,
    }, id);

    setChecklist((prev) => ({
      ...newChecklist,
      updateCount: prev.updateCount,
    }));
  };

  const overrideProduct = async (payload: ChecklistOverrideProductPayload) => {
    const newChecklist = await postOverrideProduct(payload, id);

    setChecklist((prev) => ({
      ...newChecklist,
      updateCount: prev.updateCount,
    }));

    fetchTransitionStatus();
  };

  const uploadChecklistDocuments = async (documentPayload: UploadChecklistDocumentPayload): Promise<Checklist> => {
    const result = await uploadDocuments(id, documentPayload);
    setChecklist((prev) => ({
      ...prev,
      ...result,
    }));

    fetchTransitionStatus();

    return result;
  };

  const sendOffer = async (verificationTypes?: string[]) => {
    const { offer } = checklist?.sendOffer ?? {};

    const sendOfferPayload: SendOfferPayload = {
      typeOfSignature: offer?.offerType!,
      verificationTypes,
    };

    const result = await postSendOffer(id, sendOfferPayload);
    fetchTransitionStatus();
    setChecklist((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const cancelOffer = async () => {
    const result = await postCancelOffer(id);
    fetchTransitionStatus();
    setChecklist((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const resendOffer = async () => {
    const { sessionId } = checklist?.sendOffer?.offer ?? {};

    await postResendOffer(id, sessionId ?? '');
    fetchTransitionStatus();
  };

  const runAvmSearch = async (avmType: AvmSearchTypes) => {
    const result = await postAVMSearch(id, avmType);
    fetchTransitionStatus();
    setChecklist((prev) => ({
      ...prev,
      ...result,
    }));
  };
  const { addLoader, removeLoader } = useLoader();
  useEffect(() => {
    (async () => {
      addLoader();
      await fetchChecklist();
      removeLoader();
    })();
  }, [id]);

  return {
    checklist,
    historicData,
    setChecklistData: setChecklist,
    updateChecklist,
    fetchChecklist,
    postNewNote,
    overrideProduct,
    uploadChecklistDocuments,
    sendOffer,
    cancelOffer,
    resendOffer,
    runAvmSearch,
  };
}

export const useChecklistAutoSave = () => {
  const didUpdate = useDidUpdate();
  const { id, checklist, setChecklistData } = useChecklistContext();
  const { fetchTransitionStatus } = useAccountContext();

  const { putChecklist } = useChecklistApi();

  const propertiesToOmit = [
    'status', 'stage', 'isLocked', 'checklistIndicatorType',
    'possibleDecisions', 'canSubmit', 'submittedById', 'submittedByName',
    'submittedTimestamp', 'notes', 'brokerSelectedProduct',
  ];

  const deepOmit = (obj: any, keys: string[]): ChecklistRequest => _.transform(obj, (result: any, value: any, key: string) => {
    if (['errors', 'warnings', 'updateCount', 'dirtyCount'].includes(key)) return;

    if (key === 'product') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { brokerSelectedProduct, underwriterSelectedProduct, ...rest } = value ?? {
        brokerSelectedProduct: { ...defaultSelectedProduct },
        underwriterSelectedProduct: { ...defaultSelectedProduct },
      };

      result[key] = {
        ..._.omit(rest, keys),
        underwriterSelectedProduct: _.pick(underwriterSelectedProduct, ['id', 'productCode']),
      };

      return;
    }

    result[key] = _.isObject(value) ? _.omit(value, keys) : value;
  });

  const checklistRequest = deepOmit(checklist, propertiesToOmit);

  useEffect(() => {
    if (!didUpdate) return;
    (async () => {
      const { updateCount, dirtyCount } = checklist;
      const newChecklist = await putChecklist(checklistRequest, id);
      fetchTransitionStatus();

      setChecklistData((prev: Checklist) => {
        if (updateCount >= prev?.updateCount && dirtyCount >= prev?.dirtyCount) {
          return {
            ...newChecklist,
            updateCount,
          };
        }
        return prev;
      });
    })();
  }, [checklist?.updateCount]);
};
